import { Component, OnDestroy, OnInit } from '@angular/core';
import { ErrorParser } from '@app/classes';
import { AuthorizationDocument } from '@app/models/payroll/self-onboarding-document.model';
import { SelfOnboardingDocumentUploader } from '@app/modules/payroll/interfaces/self-onboarding/self-onboarding-document-uploader.interface';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { SelfOnboardingDocumentsService } from '@app/modules/payroll/services';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import {
    AuthorizationDocumentType,
    PAYROLL_AUTHORIZATION_DOCUMENTS,
} from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { OnboardingStatusService } from '@app/modules/self-serve/services/onboarding-status.service';
import { AuthService, FileHelperService, NotifyService, PayrollSettingsService } from '@app/services';
import { Settings } from '@app/services/payroll-settings.service';
import { Subscription } from 'rxjs';

type ParsableError = Parameters<typeof ErrorParser.parse>[0];

@Component({
    selector: 'app-self-onboarding-authorization-documents',
    templateUrl: './authorization-documents.template.html',
    styleUrls: ['./authorization-documents.style.scss'],
})
export class SelfOnboardingAuthorizationDocumentsView implements OnInit, OnDestroy {
    isLoading = true;
    humiToSubmitROEs = false;
    roesHaveBeenUploaded = false;
    currentFileBeingUploaded?: AuthorizationDocumentType;
    padAgreementDocument?: AuthorizationDocument;
    roeAuthorizationDocument?: AuthorizationDocument;
    settings?: Settings;
    subscriptions: Subscription[] = [];

    constructor(
        private notify: NotifyService,
        private selfOnboardingDocumentsService: SelfOnboardingDocumentsService,
        private fileHelper: FileHelperService,
        private auth: AuthService,
        private payrollSettingsService: PayrollSettingsService,
        private onboardingStatusService: OnboardingStatusService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {}

    async ngOnInit(): Promise<void> {
        this.subscriptions.push(
            this.payrollSettingsService.settings$.subscribe((settings) => {
                this.settings = settings;
            })
        );
        await this.getDocuments();
        this.isLoading = false;
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    async getDocuments(): Promise<void> {
        try {
            const authorizationDocuments: Record<AuthorizationDocumentType, AuthorizationDocument> =
                await this.selfOnboardingDocumentsService.getDocuments(PAYROLL_AUTHORIZATION_DOCUMENTS);
            Object.entries(authorizationDocuments).some(([key, value]) => {
                if (key === 'PadAgreement') {
                    this.padAgreementDocument = value;
                }
                if (key === 'ROEAuthorization') {
                    this.roeAuthorizationDocument = value;
                }
            });
        } catch (error) {
            this.notify.error(ErrorParser.parse(error as ParsableError));
        }
    }

    async onDownloadTemplate(templateType: string): Promise<void> {
        const filePath = PayrollResources.SelfOnboardingDocumentDownload;
        const fileName = templateType === 'pad' ? 'pre-authorized-debit-agreement-form' : 'roe-consent-form';
        const param = { templateName: templateType };
        return new Promise((resolve, reject) => {
            this.fileHelper.savePayrollFile(filePath, fileName, 'pdf', param, (e?: Record<string, unknown>) => {
                if (e) {
                    this.notify.error(ErrorParser.parse(e));
                    reject(e);
                } else {
                    resolve();
                }
            });
        });
    }

    async onDownloadSignedAgreement(document?: AuthorizationDocument): Promise<void> {
        if (!document) return;

        const filePath = PayrollResources.SelfOnboardingDocumentDownloadSignedForm.replace(
            ':documentId',
            document.id.toString()
        );
        const fileName =
            document.type === 'PadAgreement' ? 'signed-pre-authorized-debit-agreement-form' : 'signed-roe-consent-form';
        return new Promise((resolve, reject) => {
            this.fileHelper.savePayrollFile(filePath, fileName, 'pdf', null, (e?: ParsableError) => {
                if (e) {
                    this.notify.error(ErrorParser.parse(e));
                    reject(e);
                } else {
                    resolve();
                }
            });
        });
    }

    async onUploadAuthorizationDocument(file?: File): Promise<void> {
        if (!this.currentFileBeingUploaded) return;

        const uploaderInfo: SelfOnboardingDocumentUploader = {
            documentUploaderEmailAddress: this.auth?.employee?.account?.email ?? '',
            documentUploaderFirstName: this.auth?.employee?.firstName ?? '',
            documentUploaderLastName: this.auth?.employee?.lastName ?? '',
        };
        try {
            if (!file) {
                (document.querySelector('[name="fileSelector"]') as HTMLElement).click();
                return;
            }
            this.isLoading = true;
            await this.selfOnboardingDocumentsService.uploadFile(this.currentFileBeingUploaded, file, uploaderInfo);
        } catch (e) {
            this.notify.error('payroll.setup.anErrorOccurredWhileUploadingTheDocument');
        }
        (document.querySelector('[name="fileSelector"]') as HTMLInputElement).value = '';
        await this.getDocuments();
        this.onboardingStatusService.refresh();

        this.isLoading = false;
    }

    async saveRoeSubmissionState(willSubmitViaHumi?: boolean): Promise<void> {
        if (!this.settings) return;

        this.settings.allowAutomaticRoeSubmissions = false;
        if (willSubmitViaHumi) {
            this.settings.informPreviousPayrollProviderToSubmitRoe = false;
        }

        try {
            await this.payrollSettingsService.save();
        } catch (error) {
            this.notify.error(ErrorParser.parse(error as ParsableError));
        }
    }
}
